import type { ReactNode } from 'react'
import cx from 'classnames'
import { Avatar } from '@/components/Avatar'
import { Icon } from '@/components/Icon'
import styles from './TableCell.module.css'
import { dateFormatters } from '@/utils/date-util'

/**
 * Density presets for table columns.
 */
export type ColumnDensity = 'Default' | 'Comfortable' | 'Compact'

/**
 * Props of a cell that renders its children.
 */
export interface CellProps {
  children: ReactNode
}

/**
 * Props of a cell that takes a test ID.
 */
export interface TestIdProps {
  testId?: string
}

/**
 * Props of a cell that aligns its content.
 */
export interface AlignmentProps {
  alignment?: 'left' | 'center' | 'right'
}

/**
 * The text inside a text cell.
 */
export function TextCellContent({ children, testId }: CellProps & TestIdProps) {
  return <span data-test={testId}>{children}</span>
}

/**
 * Formats a date for a date cell.
 */
export type FormatDate = (date?: Date | number) => string

interface DateDisplayProps {
  date: Date
  density: ColumnDensity
  format?: FormatDate
}

/**
 * Correctly formats dates of the form [Month] [Day], [Year] for table cells.
 */
export function DateDisplay({ date, density, format }: DateDisplayProps) {
  const fullSize = density === 'Default'
  const compact = density === 'Compact'
  const formatDate: FormatDate =
    format ??
    (compact
      ? dateFormatters.dateNumeric.format
      : dateFormatters.dateLong.format)

  return (
    <>
      <span
        className={cx(
          styles.dateLine,
          fullSize ? styles.medium : styles.normal,
        )}
      >
        {formatDate(date)}
      </span>
      {fullSize && (
        <span className={styles.timeLine}>
          {dateFormatters.timeDefault.format(date)}
        </span>
      )}
    </>
  )
}

/**
 * Props of an image cell.
 */
export interface ImageCellProps {
  title?: string | null
  image?: string | null
  imageFallback?: string | null
  size?: number
  minWidth?: number
  noPadding?: boolean
  online?: boolean
  testId?: string
}

/**
 * The avatar and title inside an image cell.
 */
export function ImageCellContent({
  title,
  image,
  imageFallback,
  size,
  minWidth,
  noPadding,
  testId,
  online,
}: ImageCellProps) {
  return (
    <>
      {(image || title) && (
        <div className={styles.primaryCellContainer} style={{ minWidth }}>
          <div
            className={cx(styles.imageContainer, noPadding && styles.noPadding)}
          >
            <Avatar
              size={size ?? 20}
              subject={{
                avatar: image,
                avatarFallback: imageFallback,
              }}
            />
          </div>
          <div className={styles.textCell} data-test={testId}>
            {title}
          </div>
          {online && (
            <div className={cx(styles.presenceIndicator, styles.online)} />
          )}
        </div>
      )}
    </>
  )
}

/**
 * Props of a check cell.
 */
export interface CheckCellProps {
  checked?: boolean
  color?: string
}

/**
 * The check mark inside a check cell.
 */
export function CheckCellContent({ checked, color }: CheckCellProps) {
  return (
    <div className={styles.textCenter}>
      {checked ? <Icon name="tick" size={24} color={color} /> : null}
    </div>
  )
}
